import type { Tour } from './tour-types';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { TOURS } from './definitions';
import { pickTour, stepsFor, tourForRoute, versionFor } from './tour-rules';
import { waitForAnchors } from './anchors';
import { useTours } from './use-tours';
import { useCurrentTourScreen } from './use-tour-screen';

/** Where the user is, for judging a tour: the route, plus the tab on screens that report one. */
const usePlace = () => {
  const { pathname } = useLocation();
  const screen = useCurrentTourScreen();
  return { pathname, screen, place: screen ? `${pathname}?screen=${screen}` : pathname };
};

/** Whether any of the screen's data is still being fetched. */
const useIsLoading = () => {
  const queryClient = useQueryClient();
  return () => queryClient.isFetching() > 0;
};

/**
 * Starts a tour on its own when one is due. Mounted once, in the dashboard shell.
 *
 * A route (and a tab, on screens that report one) is judged once per visit. When the
 * platform tour finishes, the page under it does not then start its own tour: two tours
 * back to back is exactly the intrusion this is meant to avoid. The page's tour waits for
 * the next time it is opened.
 */
export const useTourTrigger = () => {
  const { pathname, screen, place } = usePlace();
  const isLoading = useIsLoading();
  const { user } = useAuth();
  const { state, running, setRunning, markSeen } = useTours();
  const judged = useRef<string | null>(null);
  const cancel = useRef<(() => void) | null>(null);

  // Leaving the page or the tab drops a tour that was still waiting for it to load.
  useEffect(() => () => cancel.current?.(), [place]);

  useEffect(() => {
    if (!state || !user || running || judged.current === place) return;
    judged.current = place;

    const picked = pickTour({ tours: TOURS, pathname, screen, state, user });
    if (!picked) return;

    const { tour, seenVersion, version } = picked;
    const steps = stepsFor({ tour, seenVersion, user });

    // Nothing in it this user can use: recorded, so it is not weighed again on every visit.
    if (!steps.length) {
      markSeen({ tourKey: tour.key, version, outcome: 'suppressed' });
      return;
    }

    cancel.current = waitForAnchors({
      steps,
      holdForFirstRun: !!tour.routes,
      isLoading,
      onReady: (visible) => {
        // The screen never showed any of it, on this visit. Not recorded, so the next visit tries again.
        if (!visible.length) return;
        setRunning({ tour, version, steps: visible, isUpdate: seenVersion > 0, isReplay: false });
      },
    });
  }, [place, state, user, running]);
};

/**
 * The Help menu's replays: everything current, and nothing recorded. A replay still
 * waiting for its screen is dropped when the user moves on, so it cannot start on the
 * next page, which may well carry the same anchors.
 */
export const useTourReplay = () => {
  const { pathname, screen, place } = usePlace();
  const isLoading = useIsLoading();
  const { user } = useAuth();
  const { running, setRunning } = useTours();
  const cancel = useRef<(() => void) | null>(null);

  useEffect(() => () => cancel.current?.(), [place]);

  const pageTour = tourForRoute({ tours: TOURS, pathname, screen });
  const platform = TOURS.find((tour) => tour.key === 'platform') ?? null;

  const replay = (tour: Tour | null) => {
    if (!tour || running) return;
    const steps = stepsFor({ tour, seenVersion: 0, user, isReplay: true });
    cancel.current?.();
    cancel.current = waitForAnchors({
      steps,
      holdForFirstRun: false,
      isLoading,
      onReady: (visible) => {
        if (visible.length) {
          setRunning({ tour, version: versionFor({ tour, user }), steps: visible, isUpdate: false, isReplay: true });
        }
      },
    });
  };

  return {
    hasPageTour: !!pageTour,
    replayPlatform: () => replay(platform),
    replayPage: () => replay(pageTour),
  };
};
